import { PdfLayout } from '../PdfLayout';
import { Code2, Play, Bug, BookOpen, Blocks } from 'lucide-react';

const benefits = [
  {
    icon: Code2,
    title: 'Sandboxed JavaScript',
    text: 'Modern JavaScript with top-level await, running on the appliance in an isolated sandbox. Scripts keep working when nobody is logged in.',
  },
  {
    icon: Play,
    title: 'Event-Driven Execution',
    text: 'Run on an interval or cron schedule, a tag change or a threshold crossing (with hysteresis), a device connecting or dropping, startup or shutdown, or a message from another script — or from a button, workflow or alarm pipeline.',
  },
  {
    icon: Blocks,
    title: 'Built-In API',
    text: 'Read and write tags, call web services over HTTP, query the historian, read and write Data Tables, run named queries, send email, start and count work orders, set timers, and start workflows and ladder tasks.',
  },
  {
    icon: Bug,
    title: 'Run Log and Version History',
    text: 'Every run is logged with its output, duration and any error with a call stack on your own line numbers, and every tag write is recorded. Restore any of the last 50 saved versions.',
  },
  {
    icon: BookOpen,
    title: 'Script Libraries',
    text: 'Put shared functions in a library once and call them by name from every script.',
  },
];

export function JsScripting() {
  return (
    <PdfLayout
      category="Extensibility"
      headline="Extend Everything with"
      headlineHighlight="JavaScript Scripting"
      description="When a diagram isn't enough, drop into JavaScript. Write calculations, parse device replies, loop over many tags and talk to web services and databases — all from the built-in editor, with autocomplete for the scripting API and your tag paths."
      accentColor="#ca8a04"
      benefits={benefits}
    >
      <ScriptMockup />
    </PdfLayout>
  );
}

const K = 'text-[#7c3aed]'; // keyword
const V = 'text-[#06b6d4]'; // identifier
const P = 'text-[#94a3b8]'; // punctuation
const F = 'text-[#ca8a04]'; // function
const S = 'text-[#16a34a]'; // string
const N = 'text-[#3b82f6]'; // number
const C = 'text-[#64748b]'; // comment
const W = 'text-[#e2e8f0]'; // plain

function ReadLine({ name, tag }: { name: string; tag: string }) {
  return (
    <div className="mt-0.5">
      <span className={K}>const</span> <span className={V}>{name}</span> <span className={P}>=</span>{' '}
      <span className={W}>Number</span><span className={P}>(</span><span className={V}>tags</span><span className={P}>.</span>
      <span className={F}>read</span><span className={P}>(</span><span className={S}>'CNC-01'</span><span className={P}>, </span>
      <span className={S}>'{tag}'</span><span className={P}>));</span>
    </div>
  );
}

function ScriptMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Script Editor — OEE Calculator</span>
      </div>

      {/* Code block */}
      <div className="bg-[#1e293b] rounded-lg p-3 font-mono text-[7px] leading-[1.6] mb-2">
        <ReadLine name="run" tag="RunMin" />
        <ReadLine name="good" tag="Good" />
        <ReadLine name="total" tag="Total" />
        <div className="mt-2"><span className={C}>// Availability x quality, 8 h shift</span></div>
        <div className="mt-0.5">
          <span className={K}>const</span> <span className={V}>oee</span> <span className={P}>= (</span><span className={V}>run</span>{' '}
          <span className={P}>/</span> <span className={N}>480</span><span className={P}>) * (</span><span className={V}>good</span>{' '}
          <span className={P}>/</span> <span className={V}>total</span><span className={P}>) *</span> <span className={N}>100</span><span className={P}>;</span>
        </div>
        <div className="mt-2">
          <span className={V}>tags</span><span className={P}>.</span><span className={F}>write</span><span className={P}>(</span>
          <span className={S}>'CNC-01/OEE'</span><span className={P}>, </span><span className={V}>oee</span><span className={P}>.</span>
          <span className={F}>toFixed</span><span className={P}>(</span><span className={N}>1</span><span className={P}>));</span>
        </div>
        <div className="mt-0.5">
          <span className={K}>await</span> <span className={V}>http</span><span className={P}>.</span><span className={F}>post</span><span className={P}>(</span>
          <span className={S}>'http://mes/api/oee'</span><span className={P}>, {'{ '}</span><span className={W}>oee</span><span className={P}>{' });'}</span>
        </div>
        <div className="mt-0.5">
          <span className={F}>log</span><span className={P}>(</span><span className={S}>'OEE'</span><span className={P}>, </span>
          <span className={V}>oee</span><span className={P}>.</span><span className={F}>toFixed</span><span className={P}>(</span>
          <span className={N}>1</span><span className={P}>));</span>
        </div>
      </div>

      {/* Run log */}
      <div className="glass-card p-2">
        <span className="text-[7px] text-text-muted block mb-1">Log</span>
        <div className="font-mono text-[7px] space-y-0.5">
          <div className="text-text-secondary">TAG WRITE: Local Tags › CNC-01/OEE = 80.1 (was 79.6)</div>
          <div className="text-brand-primary font-medium">OEE 80.1</div>
          <div className="text-status-running">&#10003; completed · 84 ms</div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">Trigger: Interval 60000 ms</span>
        <span className="text-[8px] text-status-running font-medium">● Enabled</span>
      </div>
    </div>
  );
}
